import {
  ColumnDef,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  Table,
  Row,
  Column,
  OnChangeFn,
  RowSelectionState,
  ColumnOrderState,
  ColumnSizingState,
  VisibilityState,
  PaginationState,
  SortingState,
  ColumnFiltersState,
  ExpandedState,
} from '@tanstack/react-table';
import type { RTablePaginationProps } from '../RTable.props';
import { buildExpandingConfig } from './expandingOptions';

interface UseTableInstanceProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  enableRowSelection: boolean | ((row: Row<T>) => boolean);
  enableColumnFilters: boolean;
  rowSelection: RowSelectionState;
  onRowSelectionChange: OnChangeFn<RowSelectionState>;
  onPaginationChange?: (pagination?: RTablePaginationProps) => void;
  /** Не передан — в древовидном режиме раскрытие разрешено строкам с детьми */
  getRowCanExpand?: (row: Row<T>) => boolean;
  /** Дерево строк: дети раскрываются как строки той же таблицы */
  getSubRows?: (originalRow: T, index: number) => T[] | undefined;
  /** Контролируемое раскрытие (не передано — состоянием владеет TanStack) */
  expanded?: ExpandedState;
  onExpandedChange?: OnChangeFn<ExpandedState>;
  /** Есть ли expandable rows — для управления columnOrder */
  hasExpandableRows: boolean;
  getRowId: (originalRow: T, index: number, parent?: Row<T>) => string;
  onUpdateTableData: (props: { row: Row<T>; column: Column<T, unknown>; value: unknown }) => void;
  skipAutoResetPageIndex: () => void;

  // State из контроллера
  columnOrder: ColumnOrderState;
  columnSizing: ColumnSizingState;
  columnVisibility: VisibilityState;
  paginationState: PaginationState;
  sorting: SortingState;
  columnFilters: ColumnFiltersState;

  // Setters из контроллера
  setColumnOrder: OnChangeFn<ColumnOrderState>;
  setColumnSizing: OnChangeFn<ColumnSizingState>;
  setColumnVisibility: OnChangeFn<VisibilityState>;
  setPaginationState: OnChangeFn<PaginationState>;
  setSorting: OnChangeFn<SortingState>;
  setColumnFilters: OnChangeFn<ColumnFiltersState>;
}

/**
 * Default column configuration for all table columns
 */
function getDefaultColumn<T>(): Partial<ColumnDef<T>> {
  return {
    cell: ({ getValue }) => String(getValue() ?? ''),
    enableSorting: false,
  } as Partial<ColumnDef<T>>;
}

/**
 * Custom hook that creates and configures the React Table instance
 */
export function useTableInstance<T>({
  data,
  columns,
  enableRowSelection,
  enableColumnFilters,
  rowSelection,
  onRowSelectionChange,
  onPaginationChange,
  getRowCanExpand,
  getSubRows,
  expanded,
  onExpandedChange,
  hasExpandableRows,
  getRowId,
  onUpdateTableData,
  skipAutoResetPageIndex,
  // State из контроллера
  columnOrder,
  columnSizing,
  columnVisibility,
  paginationState,
  sorting,
  columnFilters,
  // Setters из контроллера
  setColumnOrder,
  setColumnSizing,
  setColumnVisibility,
  setPaginationState,
  setSorting,
  setColumnFilters,
}: UseTableInstanceProps<T>): Table<T> {
  // Серверная пагинация: RTable сообщает о смене страницы наружу, а нарезку
  // делает источник данных. Тот же признак, что у manualPagination ниже.
  const isManualPagination = typeof onPaginationChange === 'function';

  // Опции раскрытия собраны отдельной чистой функцией — она же под тестом
  // (две регрессии случились именно здесь), см. expandingOptions.ts.
  const expanding = buildExpandingConfig<T>({
    getSubRows,
    isManualPagination,
    expanded,
    onExpandedChange,
  });

  const table = useReactTable<T>({
    data,
    columns,
    columnResizeMode: 'onChange',
    columnResizeDirection: 'ltr',
    autoResetExpanded: !skipAutoResetPageIndex,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    autoResetPageIndex: false, // We handle this manually with useSkipper
    defaultColumn: getDefaultColumn<T>(),
    state: {
      columnOrder: [
        ...(enableRowSelection ? ['select'] : []),
        ...(hasExpandableRows ? ['expander'] : []),
        ...columnOrder,
      ],
      columnSizing,
      columnVisibility,
      pagination: paginationState,
      ...expanding.state,
      rowSelection,
      sorting,
      columnFilters,
    },
    meta: {
      updateData: (props: { row: Row<T>; column: Column<T, unknown>; value: unknown }) => {
        skipAutoResetPageIndex();
        onUpdateTableData({
          row: props?.row,
          column: props?.column,
          value: props?.value,
        });
      },
    },
    rowCount: data?.length,
    manualPagination: isManualPagination,
    manualSorting: true,
    enableRowSelection,
    enableColumnFilters,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: setPaginationState,
    onSortingChange: setSorting,
    onRowSelectionChange,
    onColumnFiltersChange: setColumnFilters,
    getRowCanExpand,
    getSubRows,
    ...expanding.options,
    getRowId,
  });

  return table;
}
